<template>
    <div class="step-review">
        <h4 class="step-title">Review & Apply</h4>
        <p class="step-description">
            Review the conversion summary below. You can create the AI agent as a draft or activate it immediately.
        </p>

        <div class="review-container">
            <!-- Summary -->
            <div class="summary-card">
                <h5>
                    <i class="el-icon-document"></i>
                    Summary
                </h5>

                <div class="summary-columns">
                    <!-- Left Column: Pricing -->
                    <div class="summary-column">
                        <div class="summary-row">
                            <label>Voice Model:</label>
                            <span>{{ voiceModelName }}</span>
                        </div>
                        <div class="summary-row">
                            <label>Tier:</label>
                            <el-tag 
                                :type="getTierTagType(voiceModelTier)" 
                                size="small">
                                {{ voiceModelTier }}
                            </el-tag>
                        </div>
                        <div class="summary-row">
                            <label>Price per Minute:</label>
                            <span class="price-text">${{ voiceModelPrice.toFixed(2) }}/min</span>
                        </div>
                    </div>

                    <!-- Right Column: Conversion Summary -->
                    <div class="summary-column">
                        <div class="summary-row">
                            <label>IVR Name:</label>
                            <span>{{ analysisData?.ivr?.name || 'Legacy IVR' }}</span>
                        </div>

                        <div class="summary-row">
                            <label>New AI Agent Name:</label>
                            <span>IVA: {{ analysisData?.ivr?.name || 'Converted Agent' }}</span>
                        </div>

                        <div class="summary-row">
                            <label>Language:</label>
                            <el-tag size="small">{{ getLanguageName(wizardData.language) }}</el-tag>
                        </div>

                        <div class="summary-row">
                            <label>Voice:</label>
                            <el-tag size="small">{{ formatVoiceId(wizardData.voice_id) }}</el-tag>
                        </div>

                        <div v-if="wizardData.target_inbox_tag" class="summary-row">
                            <label>Communication Tag:</label>
                            <el-tooltip v-if="tagName" :content="tagName" placement="bottom">
                                <el-tag size="small" class="tag-ellipsis">
                                    <i class="fa fa-circle mr-1" :style="{ color: tagColor }"></i>{{ truncateTagName(tagName) }}
                                </el-tag>
                            </el-tooltip>
                            <span v-else>Loading tag...</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- AI Agent Greeting -->
            <div class="summary-card">
                <h5>
                    <i class="el-icon-chat-dot-round"></i>
                    AI Agent Greeting
                </h5>
                <div class="greeting-preview">
                    {{ wizardData.greeting || 'No greeting configured' }}
                </div>
            </div>

            <!-- Consent Section -->
            <div class="consent-card">
                <h5>
                    <i class="el-icon-magic-stick"></i>
                    Convert your legacy IVR to an AloAi Intelligent Voice Assistant
                </h5>
                
                <div class="consent-content">
                    <p class="consent-description">
                        Enable the AloAi IVA Conversion to automatically upgrade your legacy IVR into an Intelligent Virtual Assistant that can understand natural conversations and handle customer requests.
                    </p>
                    
                    <div class="conversion-benefits">
                        <div class="benefit-item">
                            <i class="el-icon-check"></i>
                            <span>A new AloAi Voice Agent is created and enabled.</span>
                        </div>
                        <div class="benefit-item">
                            <i class="el-icon-check"></i>
                            <span>Your existing IVR menu and flows are intelligently converted.</span>
                        </div>
                        <div class="benefit-item">
                            <i class="el-icon-check"></i>
                            <span>Previous settings are preserved and can be restored at any time.</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import * as AloAi from '../../constants/aloai'

export default {
    name: 'Step4Review',

    props: {
        ivrId: {
            type: Number,
            required: true
        },
        wizardData: {
            type: Object,
            required: true
        },
        analysisData: {
            type: Object,
            default: null
        },
        conversionResult: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            tagName: null,
            tagColor: null,
            consentGiven: false,
            hasAcceptedConsentBefore: false,
            // Voice model tier mapping (from AloAiBot::MODEL_TIER_MAPPING)
            MODEL_TIER_MAPPING: {
                [AloAi.VOICE_MODEL_CLAUDE_37_SONNET]: 1, // TIER_BASIC
                [AloAi.VOICE_MODEL_GEMINI_20_FLASH]: 1,
                [AloAi.VOICE_MODEL_GEMINI_20_FLASH_LITE]: 1,
                [AloAi.VOICE_MODEL_GPT_4O]: 1,
                [AloAi.VOICE_MODEL_GPT_4O_MINI]: 1,
                [AloAi.VOICE_MODEL_GPT_41]: 1,
                [AloAi.VOICE_MODEL_GPT_41_MINI]: 1,
                [AloAi.VOICE_MODEL_GPT_41_NANO]: 1,
                [AloAi.VOICE_MODEL_CLAUDE_35_HAIKU]: 1,
                [AloAi.VOICE_MODEL_CLAUDE_3_HAIKU]: 1,
                [AloAi.VOICE_MODEL_GPT_REALTIME_MINI]: 2, // TIER_PREMIUM
                [AloAi.VOICE_MODEL_GPT_REALTIME_15]: 3, // TIER_ULTRA_PREMIUM
            }
        }
    },

    computed: {
        voiceModelName() {
            // Get voice model name from Vuex store
            const voiceModels = this.$store.state.aloai?.voiceModels || []
            const model = voiceModels.find(m => m.value === this.wizardData.voice_model)
            return model ? model.name : 'Unknown Model'
        },

        voiceModelTier() {
            const tier = this.getTierForVoiceModel(this.wizardData.voice_model)
            const tierLabels = {
                1: 'Basic',
                2: 'Premium',
                3: 'Ultra Premium'
            }
            return tierLabels[tier] || 'Basic'
        },

        voiceModelPrice() {
            const tier = this.getTierForVoiceModel(this.wizardData.voice_model)
            const company = this.$store.getters['cache/getCurrentCompany']
            const rate = company?.rate
            
            switch (tier) {
                case 2: // TIER_PREMIUM
                    return rate?.aloai_voice_bot_premium_minute || 0.20
                case 3: // TIER_ULTRA_PREMIUM
                    return rate?.aloai_voice_bot_ultra_premium_minute || 0.50
                default: // TIER_BASIC
                    return rate?.aloai_voice_bot_basic_minute || 0.10
            }
        }
    },

    watch: {
        'wizardData.target_inbox_tag': {
            immediate: true,
            handler(tagId) {
                if (tagId) {
                    this.fetchTagDetails(tagId)
                } else {
                    this.tagName = null
                    this.tagColor = null
                }
            }
        }
    },

    mounted() {
        if (this.wizardData.target_inbox_tag) {
            this.fetchTagDetails(this.wizardData.target_inbox_tag)
        }
        
        // Check if consent was previously given
        this.checkStoredConsent()
    },

    methods: {
        getTierForVoiceModel(voiceModel) {
            return this.MODEL_TIER_MAPPING[voiceModel] || 1 // Default to TIER_BASIC
        },

        getTierTagType(tier) {
            const tierLower = tier ? tier.toLowerCase() : ''
            if (tierLower.includes('ultra')) return 'danger'
            if (tierLower.includes('premium')) return 'warning'
            if (tierLower.includes('basic')) return 'success'
            return 'info'
        },

        getLanguageName(code) {
            const languages = {
                'en-US': 'English (US)',
                'en-GB': 'English (GB)',
                multi: 'Multilingual (English and Spanish)',
                'es-419': 'Spanish (LATAM)',
                'es-ES': 'Spanish (ES)',
                'fr-FR': 'French',
                'de-DE': 'German',
                'it-IT': 'Italian',
                'pt-PT': 'Portuguese (Portugal)',
                'pt-BR': 'Portuguese (Brazil)',
                'nl-NL': 'Dutch',
                'zh-CN': 'Chinese',
                'ja-JP': 'Japanese',
                'ko-KR': 'Korean',
                'ru-RU': 'Russian',
                'pl-PL': 'Polish',
                'tr-TR': 'Turkish',
                'vi-VN': 'Vietnamese',
                'ro-RO': 'Romanian'
            }
            return languages[code] || code
        },

        formatVoiceId(voiceId) {
            // voiceId is now the database ID, need to look up the voice name
            if (!voiceId) return 'Unknown'
            
            // Access Vuex store to get voice details
            const voices = this.$store.state.aloai?.voices || []
            const voice = voices.find(v => v.id === voiceId)
            
            if (voice) {
                return voice.name
            }
            
            // Fallback if voice not found in store
            return 'Voice #' + voiceId
        },

        formatAction(action) {
            return action.split('_').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ')
        },

        truncateTagName(tagName) {
            if (!tagName) return ''
            if (tagName.length <= 15) return tagName
            return tagName.substring(0, 15) + '...'
        },

        async fetchTagDetails(tagId) {
            if (!tagId) return
            
            try {
                const response = await axios.get('/api/v1/tag', {
                    params: {
                        tag_ids: [parseInt(tagId)],
                        category: 2,
                        full_load: true
                    }
                })

                const tags = Array.isArray(response.data) ? response.data : response.data.data
                
                if (tags && tags.length > 0) {
                    const tag = tags[0]
                    this.tagName = tag.name
                    this.tagColor = tag.color
                }
            } catch (error) {
                console.error('Failed to fetch tag details:', error)
            }
        },

        async checkStoredConsent() {
            try {
                const response = await axios.get('/api/v1/consent/iva_conversion/check')
                if (response.data && response.data.has_consent && response.data.consent_decision === 'accepted') {
                    this.hasAcceptedConsentBefore = true
                    this.consentGiven = true
                }
            } catch (error) {
                console.warn('Could not check IVA consent status:', error)
                this.hasAcceptedConsentBefore = false
                this.consentGiven = false
            }
        },

        async handleConsentAcceptance() {
            try {
                await axios.post('/api/v1/consent/iva_conversion', {
                    accepted: true
                })
                this.consentGiven = true
                this.$emit('consent-changed', true)
            } catch (error) {
                console.error('Failed to record consent:', error)
                this.$message.error('Failed to save consent. Please try again.')
                throw error
            }
        },

        validate() {
            // No validation needed - consent will be given when user clicks "Accept & Enable"
            return true
        }
    }
}
</script>

<style scoped>
.step-review {
    padding: 10px 0 0 0;
    padding-bottom: 0px;
}

.step-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
}

.step-description {
    color: #666;
    margin-bottom: 15px;
    word-break: keep-all;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.review-container {
    padding-right: 10px;
}

.summary-card {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    padding: 20px;
    margin-bottom: 20px;
}

.summary-card h5 {
    font-size: 15px;
    font-weight: 600;
    margin-top: 0;
    margin-bottom: 15px;
    display: flex;
    align-items: center;
}

.summary-card h5 i {
    margin-right: 8px;
    font-size: 18px;
    color: #409eff;
}

.summary-card h6 {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 12px;
    margin-top: 0;
    color: #606266;
    background: none;
}

.summary-row {
    display: flex;
    margin-bottom: 16px;
    align-items: flex-start;
    gap: 8px;
}

.summary-row label {
    font-weight: 500;
    min-width: 160px;
    flex-shrink: 0;
    color: #606266;
    font-size: 14px;
}

.summary-row span {
    color: #303133;
    font-size: 14px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: keep-all;
    line-height: 1.5;
}

.summary-row .el-tag {
    display: inline-block;
    max-width: 100%;
    white-space: normal !important;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: keep-all;
    line-height: 1.5;
    padding: 4px 8px !important;
    height: auto !important;
}

.summary-row .el-tooltip {
    display: inline-block;
    max-width: 100%;
}

.greeting-preview {
    padding: 16px 18px;
    background: #fafbfc;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    color: #303133;
    font-size: 14px;
    line-height: 1.7;
    white-space: normal;
    word-break: break-word;
    min-height: 60px;
}

.actions-summary .action-count {
    display: flex;
    align-items: center;
}

.actions-summary .mt-3 {
    margin-top: 20px;
}

.actions-summary h6 {
    background: transparent !important;
    padding: 0 !important;
    border: none !important;
}

.text-success {
    color: #67c23a;
}

.text-warning {
    color: #e6a23c;
}

.text-muted {
    color: #909399;
}

.info-card {
    background: #ecf5ff;
    border-color: #d9ecff;
}

.info-card h5 {
    color: #409eff;
}

.info-card p {
    margin: 0;
    color: #666;
    line-height: 1.6;
}

.activation-option {
    margin-bottom: 15px;
}

.activation-option:last-child {
    margin-bottom: 0;
}

.activation-option strong {
    display: block;
    margin-bottom: 5px;
    color: #333;
}

.activation-option p {
    margin: 0;
    color: #666;
    font-size: 13px;
    line-height: 1.5;
}

.summary-row .el-tag .fa-circle {
    font-size: 8px;
    margin-right: 6px;
}

.tag-ellipsis {
    max-width: 200px;
    display: inline-flex;
    align-items: center;
    white-space: nowrap !important;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 4px 8px !important;
    height: auto !important;
}

.price-text {
    font-weight: 600;
    color: #409eff;
    font-size: 16px;
    background: #ecf5ff;
    padding: 4px 12px;
    border-radius: 4px;
    display: inline-block;
}

.supported-actions-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.supported-action-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    background: #f0f9ff;
    border: 1px solid #d9ecff;
    border-radius: 6px;
    transition: all 0.2s ease;
}

.supported-action-item i {
    font-size: 18px;
    flex-shrink: 0;
}

.supported-action-item .action-intent {
    font-weight: 500;
}

.unsupported-actions-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.unsupported-action-item {
    padding: 14px;
    background: #fef7e0;
    border: 1px solid #f5dab1;
    border-radius: 6px;
    transition: all 0.2s ease;
}

.action-header {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 8px;
}

.action-header i {
    font-size: 18px;
    flex-shrink: 0;
    margin-top: 2px;
}

.action-header .action-intent {
    flex: 1;
}

.action-intent {
    font-weight: 500;
    color: #303133;
    font-size: 14px;
    line-height: 1.5;
}

.action-recommendation {
    color: #909399;
    font-size: 13px;
    line-height: 1.6;
    margin-left: 28px;
    padding-left: 2px;
}

.consent-card {
    background: #fff;
    border: 2px solid #e4e7ed;
    border-radius: 8px;
    padding: 24px;
    margin-bottom: 20px;
    margin-top: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.consent-card h5 {
    font-size: 16px;
    font-weight: 600;
    margin-top: 0;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    color: #303133;
}

.consent-card h5 i {
    margin-right: 8px;
    font-size: 20px;
    color: #606266;
}

.consent-content {
    margin-top: 10px;
}

.consent-description {
    color: #606266;
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 16px;
    word-break: keep-all;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.conversion-benefits {
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.benefit-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    background: #f0f9ff;
    border-radius: 6px;
}

.benefit-item i {
    color: #67c23a;
    font-size: 16px;
    margin-top: 2px;
    flex-shrink: 0;
}

.benefit-item span {
    color: #303133;
    font-size: 14px;
    line-height: 1.5;
    flex: 1;
}

.consent-info {
    margin-top: 16px;
}

.consent-requirement {
    color: #303133;
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 8px;
}

.consent-requirement strong {
    font-weight: 700;
}

.consent-note {
    color: #606266;
    font-size: 13px;
    line-height: 1.5;
    margin: 0;
}

.summary-columns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
}

.summary-column {
    display: flex;
    flex-direction: column;
    padding: 0 10px;
}

.summary-column:first-child {
    border-right: 1px solid #e4e7ed;
    padding-right: 30px;
}

.summary-column:last-child {
    padding-left: 10px;
}

.revert-note {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 12px 16px;
    margin-top: 20px;
    text-align: center;
}

.revert-note p {
    color: #606266;
    font-size: 13px;
    line-height: 1.5;
    margin: 0;
}

@media (max-width: 768px) {
    .summary-columns {
        grid-template-columns: 1fr;
        gap: 20px;
    }
    
    .summary-column:first-child {
        border-right: none;
        border-bottom: 1px solid #e4e7ed;
        padding-right: 10px;
        padding-bottom: 20px;
        margin-bottom: 20px;
    }
    
    .summary-row label {
        min-width: 140px;
    }
}

</style>
